import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ArrowRightIcon, PaperPlaneRightIcon } from '@phosphor-icons/react';
import { Notice, PillButton } from '@/components/kit';
import { askAssistant, type AssistantReply, type ChatTurn } from '@/lib/api/assistant';
import { errorMessage } from '@/lib/api/errors';

interface Turn extends ChatTurn {
  id: number;
  reply?: AssistantReply;
}

interface AssistantChatProps {
  slug: string;
  scope: 'client' | 'owner';
  suggestions: readonly string[];
  initialQuestion?: string | null;
  bookingToken?: string | null;
  onBook?: (serviceId: string) => void;
  /** Names the service on each «Записаться» button when the answer offers several. */
  serviceName?: (serviceId: string) => string | undefined;
  intro: string;
}

/**
 * Conversation with the studio assistant. Example questions are chips that start the
 * conversation in one tap. When the assistant is unavailable the reason is shown and
 * the regular booking keeps working.
 */
export function AssistantChat({
  slug,
  scope,
  suggestions,
  initialQuestion,
  bookingToken,
  onBook,
  serviceName,
  intro,
}: AssistantChatProps) {
  const [turns, setTurns] = useState<Turn[]>([]);
  const [draft, setDraft] = useState('');
  const nextId = useRef(1);
  const asked = useRef(false);
  const endRef = useRef<HTMLDivElement | null>(null);

  const mutation = useMutation({
    mutationFn: (history: ChatTurn[]) => askAssistant({ slug, scope, messages: history, bookingToken }),
    onSuccess: (reply) => {
      setTurns((current) => [...current, { id: nextId.current++, role: 'assistant', content: reply.reply, reply }]);
    },
  });

  const send = (text: string) => {
    const content = text.trim();
    if (!content || mutation.isPending) return;
    const history = [...turns.map(({ role, content: c }) => ({ role, content: c })), { role: 'user' as const, content }];
    setTurns((current) => [...current, { id: nextId.current++, role: 'user', content }]);
    setDraft('');
    mutation.mutate(history);
  };

  useEffect(() => {
    if (initialQuestion && !asked.current) {
      asked.current = true;
      send(initialQuestion);
    }
  }, [initialQuestion]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end', behavior: 'smooth' });
  }, [turns.length, mutation.isPending]);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    send(draft);
  };

  return (
    <div className="ui-stack" style={{ ['--ui-gap' as string]: 'var(--spacing-4)' }}>
      {turns.length === 0 ? <p className="ui-muted">{intro}</p> : null}

      <div className="ui-chips" role="group" aria-label="Примеры вопросов">
        {suggestions.map((question) => (
          <button key={question} type="button" className="ui-chip" onClick={() => send(question)} disabled={mutation.isPending}>
            {question}
          </button>
        ))}
      </div>

      {turns.length > 0 ? (
        <div className="ui-stack" role="log" aria-live="polite" aria-label="Диалог с помощником">
          {turns.map((turn) =>
            turn.role === 'user' ? (
              <p key={turn.id} className="ui-msg-user">
                {turn.content}
              </p>
            ) : (
              <div key={turn.id} className="ui-msg-bot ui-stack" style={{ ['--ui-gap' as string]: '6px' }}>
                {turn.content.split(/\n+/).map((line, index) => (
                  <p key={index} className="ui-text">
                    {line}
                  </p>
                ))}
                {turn.reply?.degraded ? (
                  <span className="ui-caption">Ответ собран из данных студии без языковой модели.</span>
                ) : null}
                {onBook && turn.reply?.actions.length ? (
                  <div className="ui-chips">
                    {turn.reply.actions.slice(0, 2).map((action) => (
                      <PillButton
                        key={action.serviceId}
                        variant="primary"
                        size="sm"
                        endIcon={ArrowRightIcon}
                        onClick={() => onBook(action.serviceId)}
                      >
                        {turn.reply!.actions.length > 1 && serviceName?.(action.serviceId)
                          ? `Записаться: ${serviceName(action.serviceId)}`
                          : 'Записаться'}
                      </PillButton>
                    ))}
                  </div>
                ) : null}
              </div>
            ),
          )}
          {mutation.isPending ? (
            <span className="ui-caption" role="status">
              Смотрю данные студии…
            </span>
          ) : null}
          <div ref={endRef} />
        </div>
      ) : null}

      {mutation.isError ? <Notice title="Помощник не ответил">{errorMessage(mutation.error)}</Notice> : null}

      <form className="ui-stack" style={{ ['--ui-gap' as string]: '6px' }} onSubmit={submit}>
        <label className="ui-field__label" htmlFor={`assistant-${scope}`}>
          Сообщение
        </label>
        <div className="ui-composer">
          <input
            id={`assistant-${scope}`}
            className="ui-input"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={scope === 'client' ? 'Спросите о записи, ценах или адресе' : 'Например: что у меня завтра?'}
            enterKeyHint="send"
            autoComplete="off"
            maxLength={1000}
            disabled={mutation.isPending}
          />
          <button type="submit" className="ui-send" aria-label="Отправить" disabled={mutation.isPending || !draft.trim()}>
            <PaperPlaneRightIcon size={20} weight="fill" aria-hidden />
          </button>
        </div>
      </form>
    </div>
  );
}
